import React from 'react';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

export interface Step {
    label: string;
    description?: string;
    completed?: boolean;
    active?: boolean;
    disabled?: boolean;
}

interface StepperProps {
    steps: Step[];
    currentStep: number;
    className?: string;
    onStepClick?: (index: number) => void;
}

export function Stepper({ steps, currentStep, className, onStepClick }: StepperProps) {
    return (
        <div className={cn("w-full", className)}>
            <div className="relative flex justify-between">
                {/* Línea de conexión de fondo */}
                <div className="absolute top-4 left-0 w-full h-0.5 bg-muted -z-10" />

                {/* Línea de progreso */}
                <div
                    className="absolute top-4 left-0 h-0.5 bg-primary transition-all duration-300 -z-10"
                    style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                />

                {steps.map((step, index) => {
                    const isCompleted = index < currentStep || step.completed;
                    const isActive = index === currentStep || step.active;
                    const isClickable = onStepClick && !step.disabled && (isCompleted || isActive);

                    return (
                        <div
                            key={index}
                            className={cn(
                                "flex flex-col items-center group",
                                isClickable && "cursor-pointer"
                            )}
                            onClick={() => isClickable && onStepClick(index)}
                        >
                            <div
                                className={cn(
                                    "w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-200 bg-background",
                                    isCompleted ? "border-primary bg-primary text-primary-foreground" :
                                        isActive ? "border-primary text-primary" : "border-muted text-muted-foreground"
                                )}
                            >
                                {isCompleted ? (
                                    <Check className="w-4 h-4" />
                                ) : (
                                    <span className="text-sm font-medium">{index + 1}</span>
                                )}
                            </div>

                            <div className="mt-2 text-center">
                                <div className={cn(
                                    "text-sm font-medium transition-colors",
                                    isActive || isCompleted ? "text-foreground" : "text-muted-foreground"
                                )}>
                                    {step.label}
                                </div>
                                {step.description && (
                                    <div className="text-xs text-muted-foreground mt-0.5 hidden sm:block">
                                        {step.description}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
